import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemButton,
  Typography,
  IconButton,
  Skeleton,
  Alert,
} from '@mui/material';
import { Refresh as RefreshIcon, Email as EmailIcon } from '@mui/icons-material';
import apiClient from '../../api/client';

interface EmailMessage {
  id: string;
  thread_id: string;
  subject: string;
  sender: string;
  snippet: string;
  date: string;
  is_unread: boolean;
}

const EmailWidget: React.FC = () => {
  const [messages, setMessages] = useState<EmailMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMessages = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiClient.get<{ messages: EmailMessage[] }>('/api/gmail/messages');
      setMessages(data.messages);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    if (isToday) {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const openGmail = (msg: EmailMessage) => {
    window.open(`https://mail.google.com/mail/u/0/#inbox/${msg.thread_id}`, '_blank');
  };

  return (
    <Card
      sx={{
        height: { xs: 'auto', md: '100%' },
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardHeader
        title="Email"
        avatar={<EmailIcon />}
        action={
          <IconButton
            onClick={fetchMessages}
            disabled={loading}
            aria-label="Refresh email"
            sx={{
              width: { xs: 44, md: 32 },
              height: { xs: 44, md: 32 },
            }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        }
        titleTypographyProps={{ variant: 'h6' }}
        sx={{
          pb: 0,
          flexShrink: 0,
          '& .MuiCardHeader-content': { minWidth: 0 },
          '& .MuiCardHeader-action': { m: 0, alignSelf: 'center' },
        }}
      />
      <CardContent
        sx={{
          pt: 1,
          flexGrow: { md: 1 },
          minHeight: 0,
          overflowY: { xs: 'visible', md: 'auto' },
        }}
      >
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

        {loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} variant="text" height={48} />
          ))
        ) : messages.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No recent emails
          </Typography>
        ) : (
          <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {messages.map((msg) => (
              <ListItem key={msg.id} disablePadding>
                <ListItemButton
                  onClick={() => openGmail(msg)}
                  alignItems="flex-start"
                  sx={{
                    borderRadius: 1.5,
                    px: { xs: 1.25, md: 1 },
                    py: { xs: 1.25, md: 0.875 },
                  }}
                >
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        justifyContent: 'space-between',
                        gap: 0.5,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: msg.is_unread ? 600 : 400,
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: { xs: 2, sm: 1 },
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {msg.subject || '(no subject)'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                        {formatDate(msg.date)}
                      </Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        mt: 0.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        WebkitLineClamp: { xs: 2, sm: 1 },
                        WebkitBoxOrient: 'vertical',
                        display: '-webkit-box',
                      }}
                    >
                      {msg.sender}
                      {msg.snippet ? ` · ${msg.snippet}` : ''}
                    </Typography>
                  </Box>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );
};

export default EmailWidget;
